/**
 * 行情 SVG 图表（轻量自绘，不引 echarts）
 * type: line 折线 / bar 柱状
 */
interface ChartPoint {
  label: string;
  value: number;
}

interface PriceChartProps {
  type?: 'line' | 'bar';
  data: ChartPoint[];
  height?: number;
  color?: string;
  unit?: string;
}

function niceTicks(min: number, max: number, count = 4): number[] {
  const span = max - min || 1;
  const step = Math.ceil(span / count);
  const ticks: number[] = [];
  for (let v = Math.floor(min / step) * step; v <= max + step && ticks.length <= count; v += step) {
    ticks.push(v);
  }
  return ticks;
}

export default function PriceChart({
  type = 'line',
  data,
  height = 260,
  color = 'var(--color-teal)',
  unit = '元',
}: PriceChartProps) {
  if (!data || data.length === 0) {
    return <div className="chart-empty">暂无数据</div>;
  }

  const W = 720;
  const H = height;
  const PAD_L = 56;
  const PAD_R = 16;
  const PAD_T = 18;
  const PAD_B = 34;

  const values = data.map((d) => d.value);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const pad = (max - min || 1) * 0.12;
  const yMin = min - pad;
  const yMax = max + pad;
  const ticks = niceTicks(yMin, yMax);

  const innerW = W - PAD_L - PAD_R;
  const innerH = H - PAD_T - PAD_B;
  const x = (i: number): number => PAD_L + (data.length <= 1 ? innerW / 2 : (i * innerW) / (data.length - 1));
  const y = (v: number): number => PAD_T + innerH - ((v - yMin) / (yMax - yMin || 1)) * innerH;

  const pathPoints = data.map((d, i) => `${x(i).toFixed(1)},${y(d.value).toFixed(1)}`);
  const linePath = `M${pathPoints.join(' L')}`;

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="chart" role="img" aria-label="价格图表" preserveAspectRatio="xMidYMid meet">
      {/* 网格 + Y 轴刻度 */}
      {ticks.map((t) => (
        <g key={t}>
          <line x1={PAD_L} y1={y(t)} x2={W - PAD_R} y2={y(t)} stroke="var(--color-line)" strokeDasharray="4 4" strokeWidth="1" />
          <text x={PAD_L - 8} y={y(t) + 4} textAnchor="end" fontSize="11" fill="var(--color-ink-secondary)">
            {t}
          </text>
        </g>
      ))}

      {/* X 轴标签（最多显示 8 个） */}
      {data.map((d, i) => {
        const step = Math.ceil(data.length / 8);
        if (i % step !== 0 && i !== data.length - 1) return null;
        return (
          <text key={i} x={x(i)} y={H - 10} textAnchor="middle" fontSize="10" fill="var(--color-ink-secondary)">
            {d.label}
          </text>
        );
      })}

      {type === 'line' ? (
        <>
          <path d={linePath} fill="none" stroke={color} strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" />
          <path
            d={`${linePath} L${x(data.length - 1).toFixed(1)},${PAD_T + innerH} L${x(0).toFixed(1)},${PAD_T + innerH} Z`}
            fill={color}
            opacity="0.08"
          />
          {data.map((d, i) => (
            <circle key={i} cx={x(i)} cy={y(d.value)} r="3.2" fill={color}>
              <title>{`${d.label}: ${d.value} ${unit}`}</title>
            </circle>
          ))}
        </>
      ) : (
        <>
          {data.map((d, i) => {
            const bw = Math.min(36, (innerW / data.length) * 0.6);
            const cx = x(i);
            return (
              <g key={i}>
                <rect
                  x={cx - bw / 2}
                  y={y(d.value)}
                  width={bw}
                  height={PAD_T + innerH - y(d.value)}
                  rx={4}
                  fill={color}
                  opacity="0.85"
                >
                  <title>{`${d.label}: ${d.value} ${unit}`}</title>
                </rect>
                <text x={cx} y={y(d.value) - 6} textAnchor="middle" fontSize="11" fontWeight="600" fill="var(--color-ink)">
                  {d.value}
                </text>
              </g>
            );
          })}
        </>
      )}
    </svg>
  );
}
